import React, { useState, useEffect } from 'react';
import { Key, CheckCircle, RefreshCw, Download, ArrowUpCircle } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';

const LicenseSettings = ({ user, showSuccess, showError }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [licenseSettings, setLicenseSettings] = useState({
    licenseKey: ''
  });
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifyError, setVerifyError] = useState('');
  const [initialLicenseKey, setInitialLicenseKey] = useState('');
  const [updateCheckLoading, setUpdateCheckLoading] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(null); // null | 'current' | 'available'

  useEffect(() => {
    loadLicenseSettings();
  }, []);

  const loadLicenseSettings = async () => {
    try {
      console.log('🔄 Loading license settings...');
      const response = await fetch('/api/admin/settings', {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });
      
      if (response.ok) {
        const settings = await response.json();
        console.log('✅ Loaded settings from API:', settings);
        
        const licenseKey = settings.license_key || '';
        setLicenseSettings({
          licenseKey: licenseKey
        });
        setInitialLicenseKey(licenseKey);
        
        // Auto-verify license if one exists
        if (licenseKey.trim()) {
          autoVerifyLicense(licenseKey);
        }
      }
    } catch (error) {
      console.error('Error loading license settings:', error);
    }
  };


  const verifyLicense = async () => {
    if (!licenseSettings.licenseKey.trim()) {
      setVerifyError(t('admin.license.empty'));
      return;
    }

    setVerifyLoading(true);
    setVerifyError('');
    setVerifyResult(null);

    try {
      const currentTime = new Date().getTime();
      const response = await fetch('https://api.gineex.de/webhook/gineex', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          licenseKey: licenseSettings.licenseKey,
          challenge: currentTime.toString(),
          scope: "BANF",
          metadata: "Gineex GmbH"
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || t('admin.license.error'));
      }

      setVerifyResult(result);
      setVerifyError(''); // Clear any previous errors
      
      // Automatically save the license key after successful verification
      try {
        await autoSaveLicenseSettings();
        if (showSuccess) {
          showSuccess(t('admin.license.valid'));
        }
      } catch (saveError) {
        if (showSuccess) {
          showSuccess(t('admin.license.valid'));
        }
        if (showError) {
          showError(t('admin.license.autoSaveError'));
        }
      }
      
    } catch (error) {
      console.error('License verification error:', error);
      setVerifyError(error.message);
      setVerifyResult(null); // Clear any previous successful results
      if (showError) {
        showError(error.message);
      }
    } finally {
      setVerifyLoading(false);
    }
  };

  const autoVerifyLicense = async (licenseKey) => {
    try {
      const currentTime = new Date().getTime();
      const response = await fetch('https://api.gineex.de/webhook/gineex', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          licenseKey: licenseKey,
          challenge: currentTime.toString(),
          scope: "BANF",
          metadata: "Gineex GmbH"
        })
      });

      const result = await response.json();

      if (response.ok) {
        setVerifyResult(result);
        console.log('Auto license verification successful:', result);
      } else {
        setVerifyError(result.message || t('admin.license.autoError'));
        console.log('Auto license verification failed:', result);
      }
      
    } catch (error) {
      console.error('Auto license verification error:', error);
      setVerifyError(t('admin.license.failed', { error: error.message }));
    }
  };

  const autoSaveLicenseSettings = async () => {
    try {
      const response = await fetch('/api/admin/settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({
          licenseKey: licenseSettings.licenseKey
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || t('admin.license.autoSaveError'));
      }

      console.log('✅ License settings auto-saved successfully to backend');
      
      // Update the initial value since we just saved it
      setInitialLicenseKey(licenseSettings.licenseKey);
      
    } catch (error) {
      console.error('Auto-save license settings error:', error);
      // Don't show error to user for auto-save, just log it
      throw error; // Re-throw for the calling function to handle
    }
  };


  const handleLicenseKeyChange = (e) => {
    const newValue = e.target.value;
    setLicenseSettings({...licenseSettings, licenseKey: newValue});
    
    // Reset validation states when license key changes
    if (newValue !== initialLicenseKey) {
      setVerifyResult(null);
      setVerifyError('');
    } else {
      // If back to original value, restore original validation state
      if (initialLicenseKey.trim()) {
        autoVerifyLicense(initialLicenseKey);
      } else {
        setVerifyResult(null);
        setVerifyError('');
      }
    }
  };

  // Dummy: Nach Updates suchen
  const checkForUpdates = async () => {
    setUpdateCheckLoading(true);
    setUpdateStatus(null);
    // Simulierte Verzögerung
    await new Promise(resolve => setTimeout(resolve, 2000));
    setUpdateStatus('current');
    setUpdateCheckLoading(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left Column: Lizenzschlüssel */}
      <div className="relative group self-start">
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
          : 'glass-light-card'
        } rounded-3xl`}></div>
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
          : ''
        } rounded-3xl`}></div>

        <div className="relative p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-2xl blur-sm"></div>
                <div className="relative bg-gradient-to-br from-orange-600/60 via-orange-700/50 to-orange-800/60 backdrop-blur-xl w-10 h-10 rounded-2xl flex items-center justify-center shadow-[0_8px_32px_rgba(251,146,60,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20">
                  <Key className="w-5 h-5 text-white/90 drop-shadow-lg" />
                  <div className="absolute top-1 left-1 w-2 h-2 bg-white/30 rounded-full blur-sm"></div>
                </div>
              </div>
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'}`}>{t('admin.license.title')}</h3>
            </div>

            <button
              onClick={verifyLicense}
              disabled={verifyLoading || !licenseSettings.licenseKey.trim()}
              className={`relative group/verify overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 ${isDarkMode ? '' : 'bg-green-600 hover:bg-green-700'}`}
              style={{
                background: isDarkMode
                  ? 'linear-gradient(135deg, rgba(34,197,94,0.6) 0%, rgba(22,163,74,0.6) 50%, rgba(34,197,94,0.6) 100%)'
                  : undefined,
                backdropFilter: isDarkMode ? 'blur(20px)' : undefined,
                border: isDarkMode ? '1px solid rgba(255,255,255,0.3)' : undefined,
                boxShadow: isDarkMode
                  ? '0 8px 32px rgba(34,197,94,0.3), inset 0 1px 0 rgba(255,255,255,0.2)'
                  : undefined
              }}
            >
              <div
                className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/verify:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                style={{
                  background: isDarkMode
                    ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)'
                    : undefined
                }}
              ></div>
              <div className="relative z-10 px-4 py-2 flex items-center font-medium text-white transition-all duration-300 drop-shadow-lg">
                {verifyLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white/80 border-t-transparent mr-2"></div>
                    {t('admin.license.checking')}
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/verify:scale-110" />
                    {t('admin.license.check')}
                  </>
                )}
              </div>
            </button>
          </div>

          {/* Verify Error */}
          {verifyError && (
            <div className="mb-4 relative">
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-red-500/10 border border-red-300/30 backdrop-blur-xl'
                : 'bg-red-50 border border-red-200'
              } rounded-2xl`}></div>
              <div className="relative p-3">
                <p className={`text-sm font-medium ${isDarkMode ? 'text-red-200' : 'text-red-700'}`}>{verifyError}</p>
              </div>
            </div>
          )}

          {/* Verify Success */}
          {verifyResult && (
            <div className="mb-4 relative">
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-emerald-500/10 border border-emerald-300/30 backdrop-blur-xl'
                : 'bg-emerald-50 border border-emerald-200'
              } rounded-2xl`}></div>
              <div className="relative p-3 flex items-center space-x-3">
                <CheckCircle className={`w-5 h-5 ${isDarkMode ? 'text-emerald-200' : 'text-emerald-600'}`} />
                <div>
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-emerald-200' : 'text-emerald-700'}`}>
                    {t('admin.license.valid')}
                  </p>
                  {verifyResult.scope && (
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-emerald-300/70' : 'text-emerald-600/70'}`}>
                      {t('admin.license.licensedProduct', { scope: verifyResult.scope })}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* License Key Input */}
          <div className="mb-5">
            <label className={`block text-sm font-semibold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-700'} mb-3`}>{t('admin.license.softwareLicense')}</label>
            <div className="relative group/input">
              <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]' : 'bg-white border border-slate-200'} rounded-2xl`}></div>
              <input
                type="text"
                value={licenseSettings.licenseKey}
                onChange={handleLicenseKeyChange}
                className={`relative w-full px-4 py-4 bg-transparent border-0 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white placeholder-white/60 focus:ring-2 focus:ring-orange-400/50' : 'text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'}`}
                placeholder={t('admin.license.enter')}
              />
              <div className={`absolute inset-0 rounded-2xl border border-transparent transition-all duration-300 pointer-events-none ${isDarkMode ? 'group-focus-within/input:border-orange-400/50' : 'group-focus-within/input:border-brand/40'}`}></div>
            </div>
            <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-600'} mt-2`}>{t('admin.license.enterHint')}</p>
          </div>

          {/* Info Hint */}
          <div className="relative">
            <div className={`absolute inset-0 ${isDarkMode
              ? 'bg-orange-500/10 border border-orange-300/30 backdrop-blur-xl'
              : 'bg-orange-50 border border-orange-200'
            } rounded-2xl`}></div>
            <div className="relative p-4 flex items-start space-x-3">
              <Key className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDarkMode ? 'text-orange-200' : 'text-orange-700'}`} />
              <p className={`${isDarkMode ? 'text-orange-200/80' : 'text-orange-600'} text-sm`}>
                {t('admin.license.info')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Column: Softwareaktualisierung */}
      <div className="relative group self-start">
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20 backdrop-blur-2xl shadow-[0_8px_32px_rgba(0,0,0,0.4)]'
          : 'glass-light-card'
        } rounded-3xl`}></div>
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
          : ''
        } rounded-3xl`}></div>

        <div className="relative p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-2xl blur-sm"></div>
                <div className="relative bg-gradient-to-br from-blue-600/60 via-blue-700/50 to-blue-800/60 backdrop-blur-xl w-10 h-10 rounded-2xl flex items-center justify-center shadow-[0_8px_32px_rgba(59,130,246,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border border-white/20">
                  <ArrowUpCircle className="w-5 h-5 text-white/90 drop-shadow-lg" />
                  <div className="absolute top-1 left-1 w-2 h-2 bg-white/30 rounded-full blur-sm"></div>
                </div>
              </div>
              <div>
                <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'}`}>{t('admin.license.updateTitle')}</h3>
                <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>{t('admin.license.currentVersion')}</p>
              </div>
            </div>
          </div>

          {/* Update Check Button */}
          <div className="mb-6">
            <button
              onClick={checkForUpdates}
              disabled={updateCheckLoading}
              className={`relative group/check w-full overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 ${isDarkMode ? '' : 'bg-brand hover:bg-[#1D4ED8]'}`}
              style={{
                background: isDarkMode
                  ? 'linear-gradient(135deg, rgba(59,130,246,0.6) 0%, rgba(37,99,235,0.6) 50%, rgba(59,130,246,0.6) 100%)'
                  : undefined,
                backdropFilter: isDarkMode ? 'blur(20px)' : undefined,
                border: isDarkMode ? '1px solid rgba(255,255,255,0.3)' : undefined,
                boxShadow: isDarkMode
                  ? '0 8px 32px rgba(59,130,246,0.3), inset 0 1px 0 rgba(255,255,255,0.2)'
                  : undefined
              }}
            >
              <div
                className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/check:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                style={{
                  background: isDarkMode
                    ? 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)'
                    : undefined
                }}
              ></div>
              <div className="relative z-10 py-3 px-4 flex items-center justify-center font-medium text-white transition-all duration-300 drop-shadow-lg">
                {updateCheckLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    {t('admin.license.checkingUpdates')}
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/check:rotate-180" />
                    {t('admin.license.checkUpdates')}
                  </>
                )}
              </div>
            </button>
          </div>

          {/* Update Status */}
          {updateStatus === 'current' && (
            <div className="relative">
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-emerald-500/10 border border-emerald-300/30 backdrop-blur-xl'
                : 'bg-emerald-50 border border-emerald-200'
              } rounded-2xl`}></div>
              <div className="relative p-4 flex items-center space-x-3">
                <CheckCircle className={`w-5 h-5 flex-shrink-0 ${isDarkMode ? 'text-emerald-300' : 'text-emerald-600'}`} />
                <div>
                  <p className={`text-sm font-medium ${isDarkMode ? 'text-emerald-200' : 'text-emerald-700'}`}>
                    {t('admin.license.upToDate')}
                  </p>
                  <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-emerald-300/70' : 'text-emerald-600/70'}`}>
                    {t('admin.license.lastChecked', { date: new Date().toLocaleString() })}
                  </p>
                </div>
              </div>
            </div>
          )}

          {updateStatus === 'available' && (
            <div className="relative">
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-blue-500/10 border border-blue-300/30 backdrop-blur-xl'
                : 'bg-brand/5 border border-brand/20'
              } rounded-2xl`}></div>
              <div className="relative p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Download className={`w-5 h-5 flex-shrink-0 ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`} />
                    <div>
                      <p className={`text-sm font-medium ${isDarkMode ? 'text-blue-200' : 'text-blue-700'}`}>
                        {t('admin.license.updateAvailable')}
                      </p>
                      <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-blue-300/70' : 'text-blue-600/70'}`}>
                        {t('admin.license.updateChanges')}
                      </p>
                    </div>
                  </div>
                  <button
                    disabled
                    className={`px-4 py-2 rounded-xl text-sm font-medium ${
                      isDarkMode
                        ? 'bg-blue-600/30 text-blue-300 border border-blue-500/30 opacity-50 cursor-not-allowed'
                        : 'bg-brand/10 text-brand border border-brand/20 opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <Download className="w-4 h-4 mr-1.5 inline" />
                    {t('admin.license.install')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {!updateStatus && !updateCheckLoading && (
            <div className={`text-sm ${isDarkMode ? 'text-white/50' : 'text-slate-400'} text-center py-4`}>
              {t('admin.license.updateHint')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LicenseSettings;